import type { CompositionMetricValues } from '../../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../types';
import type {
  CompositionV3FullMetrics,
  CompositionV3GraphqlResponse,
} from './composition-v3-graphql-response';
import {
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from './is-empty-composition-bucket';

const entityLabel = (
  rowId: number | string | null | undefined,
  shortName: string
): string => (isOtherCompositionBucket(rowId) ? OTHER_BUCKET_LABEL : shortName);

type CompositionV3Payload = CompositionV3GraphqlResponse;

type MultiCompositionDataV3 = {
  dim: string;
  data: CompositionV3Payload | null | undefined;
};

export type CompositionTopNDataSetTypeV3 =
  | SingleDataSet
  | BreakdownSingleDataSet;

export const convertCompositionTopNDataV3 = ({
  data,
  isGrouped,
  metric,
  exchangeRate = 1,
}: {
  data: MultiCompositionDataV3[];
  isGrouped?: boolean;
  metric: CompositionMetricValues;
  exchangeRate?: number;
}): CompositionTopNDataSetTypeV3 => {
  if (isGrouped) {
    const compositionData: BreakdownSingleDataSet['data'] = data?.map(
      (entityGroup): BreakdownData<SingleDataPoint> => {
        const entities = entityGroup.data?.entities ?? [];
        const values = entities.map((entity) =>
          getConvertedValueV3({
            metric,
            exchangeRate,
            entityMetric: entity.metrics?.[metric],
          })
        );
        const total = values.reduce<number>(
          (sum, v) => (typeof v === 'number' ? sum + v : sum),
          0
        );

        return {
          dim: entityGroup.dim,
          breakdown: entities.map((entity, index) => {
            const value = values[index];
            const share =
              typeof value === 'number' && total > 0 ? value / total : null;

            return {
              dim: entityLabel(entity.id, entity.short_name),
              value: value ?? null,
              share,
            };
          }),
        };
      }
    );

    return { type: DataType.BREAKDOWN_SINGLE, data: compositionData };
  }

  const barDataObj =
    data?.reduce<Record<string, SingleDataPoint>>((acc, { data: payload }) => {
      payload?.entities?.forEach((entity) => {
        const metricData = entity.metrics?.[metric];
        const lastTimeseries = metricData?.[metricData.length - 1];
        if (!lastTimeseries) {
          return;
        }
        const convertedValue =
          metric === 'salary'
            ? lastTimeseries.val * exchangeRate
            : lastTimeseries.val;

        const innerDim = entityLabel(entity.id, entity.short_name);
        if (!acc[innerDim]) {
          acc[innerDim] = {
            dim: innerDim,
            value: convertedValue,
          };
        } else {
          acc[innerDim] = {
            ...acc[innerDim],
            value: (acc[innerDim]?.value ?? 0) + convertedValue,
          };
        }
      });
      return acc;
    }, {}) ?? {};

  const barData: SingleDataSet['data'] = Object.values(barDataObj).sort(
    (a, b) => (b.value ?? 0) - (a.value ?? 0)
  );

  return { type: DataType.SINGLE, data: barData };
};

const getConvertedValueV3 = ({
  metric,
  exchangeRate,
  entityMetric,
}: {
  metric: CompositionMetricValues;
  exchangeRate: number;
  entityMetric: CompositionV3FullMetrics[CompositionMetricValues] | undefined;
}): number | null => {
  if (!entityMetric?.length) {
    return null;
  }
  const lastMetric = entityMetric[entityMetric.length - 1];
  if (!lastMetric) {
    return null;
  }
  const lastVal = lastMetric.val;
  if (metric !== 'salary') return lastVal;
  return lastVal * exchangeRate;
};
